import React, {useEffect, useState} from 'react';
import {Ability, AbilityBuilder} from '@casl/ability';
import {
  Permissions,
  useAuth,
  useAuthentication,
  AbilityContext,
  LanguageService,
  useService,
} from '@innowise-group/core';
import {Employee} from '../../models';
import {PageLoader} from '../../../ui-kit';
import i18n from '../../../../i18n';
import {Subscription} from 'rxjs';

interface PersonExtended extends Employee {
  allowed_locations: {[key: string]: number[]};
  isLocal: boolean;
}

const ability = new Ability([]);

export const PersonContext = React.createContext<PersonExtended>(null);

export const AbilityProvider: React.FC = ({children}) => {
  const {authenticate} = useAuthentication();
  const {authenticated} = useAuth();
  const languageService = useService(LanguageService);
  const [isLoading, setIsLoading] = useState(true);

  const [person, setPerson] = useState<PersonExtended>(null);

  useEffect(() => {
    let authRequest: Subscription | null = null;

    const authStateSubscription = authenticated.subscribe((isAuthenticated) => {
      if (authRequest) {
        authRequest.unsubscribe();
        authRequest = null;
      }

      if (!isAuthenticated) {
        setPerson(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      authRequest = authenticate().subscribe({
        next(permissions) {
          ability.update(defineRules(permissions));
          const newPerson = {
            ...permissions.person,
            allowed_locations: permissions.allowed_locations,
            isLocal: permissions.isLocal,
          };
          setPerson(newPerson);

          const language = languageService.getAppLanguage();
          if (language) {
            i18n.changeLanguage(language);
          }

          setIsLoading(false);
        },
        error() {
          setIsLoading(false);
        },
      });
    });

    return () => {
      authStateSubscription.unsubscribe();
      authRequest?.unsubscribe();
    };
  }, [authenticate, authenticated, languageService]);

  function defineRules(authentication: Permissions) {
    const {can, rules} = new AbilityBuilder(Ability);
    const ids = authentication.ids;
    authentication.permissions.forEach((permission) => {
      const [subject, action] = permission.split('_');
      let condition = undefined;

      if (ids && ids[permission]) {
        return ids[permission].forEach((id) => {
          can(action, subject, {id: String(id)});
        });
      }
      can(action, subject, condition);
    });
    return rules;
  }

  return (
    <AbilityContext.Provider value={ability}>
      <PersonContext.Provider value={person}>{isLoading ? <PageLoader /> : children}</PersonContext.Provider>
    </AbilityContext.Provider>
  );
};
